import { useState } from 'react';
import type { JSX } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { BrandSelect } from '../lib/brands.js';
import {
  ConfirmDialog,
  EmptyState,
  ErrorState,
  Field,
  LoadingState,
  Panel,
  StatusBadge,
} from '../components/ui.js';
import '../screens-2a.css';
import type { Rights } from '../lib/session.js';

/**
 * Weby, které posílají data (SPEC-2 kap. 13.3). Klíč se ukáže jen při
 * založení a otočení — v seznamu už se nedá vyčíst.
 */

interface Site {
  id: string;
  publicId: string;
  name: string;
  seller: string;
  origins: string[];
  enabled: boolean;
  lastEventAt: string | null;
}

interface SitesResponse {
  items: Site[];
  recent: Array<{
    event_id: string;
    source: string;
    type: string;
    status: string;
    error: string | null;
    received_at: string;
  }>;
  snippetBase: string;
  webEventsEnabled: boolean;
}

/** Co e-shop musí posílat, aby hub fungoval (docs/NAPOJENI-ESHOPU.md). */
const ESHOP_EVENTS = [
  {
    type: 'order.updated',
    required: true,
    when: 'vznik objednávky, zaplacení, storno, změna položek, doručení vstupenek',
    needed: 'všechno — cesty, předstih, většina zpráv',
  },
  {
    type: 'race.updated',
    required: true,
    when: 'změna termínu, stavu prodeje, zrušení, race pack',
    needed: 'termínové zprávy a milníky',
  },
  {
    type: 'contact.updated',
    required: true,
    when: 'změna e-mailu, jazyka, telefonu, časového pásma',
    needed: 'jazyk šablony a pásmo odeslání',
  },
  {
    type: 'consent.changed',
    required: true,
    when: 'udělení nebo odvolání souhlasu',
    needed: 'marketing vůbec neodejde',
  },
  {
    type: 'gift.updated',
    required: false,
    when: 'nákup dárku, předání, uplatnění',
    needed: 'dárkové zprávy',
  },
  {
    type: 'availability.updated',
    required: false,
    when: 'změna dostupnosti kategorií',
    needed: 'upgrade a hlídací pes',
  },
  {
    type: 'watchdog.updated',
    required: false,
    when: 'přihlášení k hlídání závodu',
    needed: 'zprávy hlídacího psa',
  },
  {
    type: 'support.contact',
    required: false,
    when: 'zákazník se ozval na podporu',
    needed: 'pauza v marketingu po dotazu',
  },
  {
    type: 'message.sent',
    required: false,
    when: 'e-shop sám odeslal zprávu zákazníkovi',
    needed: 'hub o ní ví a nepošle duplicitu',
  },
];

const SAMPLE = JSON.stringify(
  {
    type: 'race.updated',
    aggregate_id: 'f1-ukazka-2027',
    version: 1,
    data: {
      race_id: 'f1-ukazka-2027',
      series: 'f1',
      season: 2027,
      name: 'Ukázkový závod',
      timezone: 'Europe/Prague',
      date_status: 'unknown',
      sales_status: 'open',
    },
  },
  null,
  2,
);

const STATUS_LABEL: Record<string, string> = {
  pending: 'Čeká',
  processed: 'Zpracováno',
  done: 'Zpracováno',
  ignored: 'Přeskočeno',
};

export function Sites({ rights }: { rights: Rights }): JSX.Element {
  const client = useQueryClient();
  const canManage = rights.can('manage_users');
  const [freshKey, setFreshKey] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [brand, setBrand] = useState('TGP');
  const [origins, setOrigins] = useState('');
  const [payload, setPayload] = useState(SAMPLE);
  const [result, setResult] = useState<string | null>(null);
  const [rotating, setRotating] = useState<Site | null>(null);

  const query = useQuery({
    queryKey: ['sites'],
    queryFn: () => api.get<SitesResponse>('/api/sites'),
  });

  const refresh = (): void => {
    void client.invalidateQueries({ queryKey: ['sites'] });
  };

  const create = useMutation({
    mutationFn: () =>
      api.post<{ key: string }>('/api/sites', {
        name,
        seller: brand,
        origins: origins
          .split(',')
          .map((o) => o.trim())
          .filter(Boolean),
      }),
    onSuccess: (data) => {
      setFreshKey(data.key);
      setName('');
      setOrigins('');
      refresh();
    },
    onError: (err: Error) => setNote(err.message),
  });

  const toggle = useMutation({
    mutationFn: (input: { id: string; enabled: boolean }) =>
      api.patch(`/api/sites/${input.id}`, { enabled: input.enabled }),
    onSuccess: refresh,
  });

  const rotate = useMutation({
    mutationFn: (id: string) => api.post<{ key: string }>(`/api/sites/${id}/rotate-key`, {}),
    onSuccess: (data) => {
      setFreshKey(data.key);
      setNote('Nový klíč platí hned, starý ještě hodinu.');
      refresh();
    },
  });

  const test = useMutation({
    mutationFn: () => api.post<unknown>('/api/sites/test-event', JSON.parse(payload)),
    onSuccess: (data) => setResult(JSON.stringify(data, null, 2)),
    onError: (err: Error) => setResult(err.message),
  });

  if (query.isLoading) return <LoadingState what="weby" />;
  if (query.isError)
    return (
      <ErrorState
        title="Weby se nepodařilo načíst"
        error={query.error}
        onRetry={() => void query.refetch()}
      />
    );
  const data = query.data;

  return (
    <>
      {note ? (
        <p className="hint" role="status">
          {note}
        </p>
      ) : null}
      {freshKey ? (
        <p className="hint" role="status">
          Klíč se zobrazí jen teď, uložte si ho: <code>{freshKey}</code>
        </p>
      ) : null}

      <Panel title="Co má e-shop posílat">
        <p className="note">
          Hub si data nestahuje sám. E-shop mu při každé změně pošle zprávu o události (objednávka,
          změna závodu, souhlas…). Níže je seznam událostí a k čemu je hub potřebuje.
        </p>
        <details className="s2a-tech">
          <summary>Technické podrobnosti pro vývojáře e-shopu</summary>
          <p>
            Události se posílají na <code>POST /v1/events</code>, podepsané klíčem (HMAC-SHA256 z{' '}
            <code>timestamp.tělo</code>, razítko nejvýš 5 minut staré).
          </p>
          <p>
            Tři pravidla: <b>event_id je jedinečné</b> (opakované odeslání je bezpečné),{' '}
            <b>version roste</b> (událost je snímek celého stavu, ne přírůstek) a{' '}
            <b>posílá se celý stav</b> (chybějící pole znamená „nevím“, ne „smaž“).
          </p>
        </details>
        <div className="scroll">
          <table>
            <thead>
              <tr>
                <th>Událost</th>
                <th>Kdy ji poslat</th>
                <th>Bez ní nefunguje</th>
              </tr>
            </thead>
            <tbody>
              {ESHOP_EVENTS.map((row) => (
                <tr key={row.type}>
                  <td>
                    <code>{row.type}</code>
                    {row.required ? (
                      <StatusBadge tone="warn" icon="alert">
                        Nutné
                      </StatusBadge>
                    ) : null}
                  </td>
                  <td className="note">{row.when}</td>
                  <td className="note">{row.needed}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="note">
          Podrobný návod s příklady těl událostí je v repozitáři:{' '}
          <code>docs/NAPOJENI-ESHOPU.md</code>.
        </p>
      </Panel>

      <Panel title="Weby">
        {!data?.webEventsEnabled ? (
          <p className="hint">
            Události z webu jsou vypnuté v nastavení hubu (<code>WEB_EVENTS_ENABLED</code>), takže
            vložený kód na webu zatím nic nepošle.
          </p>
        ) : null}
        {(data?.items ?? []).length === 0 ? (
          <EmptyState title="Zatím žádný web">
            {canManage
              ? 'Přidejte první web ve formuláři níže. Dostanete klíč a kód, který web vloží do stránek.'
              : 'Weby zakládá správce uživatelů.'}
          </EmptyState>
        ) : null}
        <div className="scroll">
          <table>
            <thead>
              <tr>
                <th>Název</th>
                <th>Veřejný kód</th>
                <th>Domény</th>
                <th>Stav</th>
                <th>Poslední událost</th>
                <th>
                  <span className="sr-only">Akce</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {(data?.items ?? []).map((site) => (
                <tr key={site.id}>
                  <td>{site.name}</td>
                  <td>
                    <code>{site.publicId}</code>
                  </td>
                  <td>{site.origins.length > 0 ? site.origins.join(', ') : 'odkudkoli'}</td>
                  <td>
                    {site.enabled ? (
                      <StatusBadge tone="ok" icon="check">
                        Zapnutý
                      </StatusBadge>
                    ) : (
                      <StatusBadge tone="warn" icon="close">
                        Vypnutý
                      </StatusBadge>
                    )}
                  </td>
                  <td>
                    {site.lastEventAt ? new Date(site.lastEventAt).toLocaleString('cs-CZ') : '—'}
                  </td>
                  <td>
                    {canManage ? (
                      <div className="row">
                        <button
                          className="btn"
                          onClick={() => toggle.mutate({ id: site.id, enabled: !site.enabled })}
                        >
                          {site.enabled ? 'Vypnout' : 'Zapnout'}
                        </button>
                        <button className="btn" onClick={() => setRotating(site)}>
                          Vyměnit klíč
                        </button>
                      </div>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {canManage ? (
          <div className="row s2a-form">
            <Field label="Název webu">
              <input value={name} onChange={(e) => setName(e.target.value)} />
            </Field>
            <Field label="Značka">
              <BrandSelect value={brand} onChange={setBrand} allLabel="Vyberte značku" />
            </Field>
            <Field label="Povolené domény (čárkou)">
              <input
                value={origins}
                placeholder="ticketsgp.cz, www.ticketsgp.cz"
                onChange={(e) => setOrigins(e.target.value)}
              />
            </Field>
            <button className="btn pri" disabled={!name} onClick={() => create.mutate()}>
              Přidat web
            </button>
          </div>
        ) : null}

        {(data?.items ?? []).length > 0 ? (
          <p className="note">
            Snippet na web:{' '}
            <code>{`<script src="${data?.snippetBase}?site=${data?.items[0]?.publicId}"></script>`}</code>{' '}
            a po souhlasu s měřením <code>hub.consent(true)</code>.
          </p>
        ) : null}
      </Panel>

      {canManage ? (
        <Panel title="Zkušební odeslání">
          <p className="note">
            Událost projde stejnou cestou jako z e-shopu nebo z webu — uvidíte, co založila nebo
            proč spadla.
          </p>
          <Field label="Událost (JSON)">
            <textarea rows={12} value={payload} onChange={(e) => setPayload(e.target.value)} />
          </Field>
          <button className="btn pri" onClick={() => test.mutate()}>
            Poslat
          </button>
          {result ? <pre className="scroll">{result}</pre> : null}
        </Panel>
      ) : null}

      <Panel title="Co přišlo">
        {(data?.recent ?? []).length === 0 ? (
          <EmptyState title="Zatím nic nepřišlo">
            Jakmile e-shop nebo web pošle první událost, objeví se tady s výsledkem zpracování.
          </EmptyState>
        ) : null}
        <div className="scroll">
          <table>
            <thead>
              <tr>
                <th>Kdy</th>
                <th>Zdroj</th>
                <th>Typ</th>
                <th>Stav</th>
                <th>Důvod</th>
              </tr>
            </thead>
            <tbody>
              {(data?.recent ?? []).map((row) => (
                <tr key={row.event_id}>
                  <td>{new Date(row.received_at).toLocaleString('cs-CZ')}</td>
                  <td>{row.source}</td>
                  <td>{row.type}</td>
                  <td>
                    {row.status === 'failed' ? (
                      <StatusBadge tone="bad" icon="alert">
                        Chyba
                      </StatusBadge>
                    ) : (
                      <StatusBadge tone="ok" icon="check">
                        {STATUS_LABEL[row.status] ?? row.status}
                      </StatusBadge>
                    )}
                  </td>
                  <td>{row.error ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>

      <ConfirmDialog
        open={rotating !== null}
        title="Vyměnit klíč webu?"
        danger
        confirmLabel="Vyměnit klíč"
        onCancel={() => setRotating(null)}
        onConfirm={() => {
          if (rotating) rotate.mutate(rotating.id);
          setRotating(null);
        }}
      >
        Web {rotating?.name} dostane nový klíč. Starý klíč ještě hodinu funguje, do té doby nový
        klíč zapište do e-shopu nebo na web.
      </ConfirmDialog>
    </>
  );
}
